/**
 * Access state machine driven by /api/me/billing.
 * Returns one of:
 *   'loading'          - still fetching
 *   'full_access'      - regular dashboard use
 *   'no_plan'          - client signed up but hasn't paid yet
 *   'cancelled'        - subscription cancelled (no override)
 *   'past_due_grace'   - payment failed <7d ago, warning banner, full access
 *   'past_due_locked'  - payment failed ≥7d ago, dashboard locked
 *
 * Super Admin `manual_override_enabled=true` always short-circuits to 'full_access'.
 */
export const GRACE_PERIOD_DAYS = 7;

export function computeAccessState(billing) {
  if (!billing) return { state: 'loading', grace_days_left: null };
  if (billing.manual_override_enabled) {
    return { state: 'full_access', override: true, grace_days_left: null };
  }

  const status = (billing.billing_status || '').toLowerCase();

  if (status === 'cancelled') {
    return { state: 'cancelled', grace_days_left: null };
  }

  if (status === 'past_due') {
    const failedIso = billing.payment_failed_at;
    if (failedIso) {
      const ageMs = Date.now() - new Date(failedIso).getTime();
      const ageDays = ageMs / (1000 * 60 * 60 * 24);
      if (ageDays >= GRACE_PERIOD_DAYS) {
        return { state: 'past_due_locked', grace_days_left: 0 };
      }
      return {
        state: 'past_due_grace',
        grace_days_left: Math.max(0, Math.ceil(GRACE_PERIOD_DAYS - ageDays))
      };
    }
    // past_due without a timestamp → treat as grace (lenient)
    return { state: 'past_due_grace', grace_days_left: GRACE_PERIOD_DAYS };
  }

  const active = billing.subscription_active || status === 'active' || status === 'trialing';
  const hasPlan = !!billing.plan_type;
  if (active && hasPlan) {
    return { state: 'full_access', grace_days_left: null };
  }

  return { state: 'no_plan', grace_days_left: null };
}

export function isRestrictedState(state) {
  return state === 'no_plan' || state === 'cancelled' || state === 'past_due_locked';
}
